import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import styled from "styled-components";
import { Loader } from "../../sharedUI/FullScreenLoader";
import IconButton from "../../sharedUI/IconButton";
import { fetcher } from "../../utils/fetcher";
import EventPlanForm from "../../components/forms/EventPlanForm";
import EventPlanCard from "../../components/cards/EventPlanCard";
import DeleteConfirmation from "../../components/forms/DeleteConfirmation";
import Input from "../../sharedUI/Input";

const StyledEventPlans = styled.div`
  .events-top {
    display: flex;
    justify-content: space-between;
    &-right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      .search-input {
        margin: 0;
      }
    }
  }
  .entries {
    margin-top: 1rem;
  }
`;

const EventPlans = () => {
  const outlet = useOutletContext();
  const { globalState, notify, modal, closeModal } = outlet;

  //State
  const [eventPlans, setEventPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchString, setSearchString] = useState("");

  //Functions
  const getEntries = async () => {
    setLoading(true);
    const { err, data } = await fetcher({
      path: "events/admin",
      auth: globalState.token,
    });
    if (err) {
      notify.error("Kunde inte hämta arrangemang");
    } else {
      setEventPlans(data);
    }
    setLoading(false);
  };

  const createEntry = async (formData, cb, errCb) => {
    const { err } = await fetcher({
      path: "events",
      body: formData,
      method: "POST",
      auth: globalState.token,
    });
    if (err) {
      errCb();
      notify.error("Kunde inte skapa arrangemang");
      return;
    }
    cb();
    closeModal();
    getEntries();
  };

  const editEntry = async (formData, cb, errCb) => {
    const { err } = await fetcher({
      path: `events/${formData._id}`,
      body: formData,
      method: "POST",
      auth: globalState.token,
    });
    if (err) {
      errCb();
      notify.error("Kunde inte uppdatera arrangemang");
      return;
    }
    cb();
    closeModal();
    getEntries();
  };

  const deleteEntry = async (entry, cb, errCb) => {
    const { err } = await fetcher({
      path: `events/${entry._id}`,
      method: "DELETE",
      auth: globalState.token,
    });
    if (err) {
      errCb();
      notify.error("Kunde inte radera arrangemang");
      return;
    }
    cb();
    closeModal();
    getEntries();
  };

  const deleteEntryInit = (entry) => {
    modal(
      <DeleteConfirmation
        item={entry}
        deleteKey={"title"}
        handleInvalidKey={() => notify.error("Fyll i rätt värde i fältet")}
        action={deleteEntry}
        name="arrangemanget"
        instruction="Arrangemangets titel"
      />,
      "Radera arrangemang"
    );
  };

  const editEntryInit = (entry) => {
    modal(
      <EventPlanForm data={entry} handleSubmit={editEntry} outlet={outlet} />,
      "Redigera arrangemang"
    );
  };

  const createEntryInit = () => {
    modal(
      <EventPlanForm handleSubmit={createEntry} outlet={outlet} />,
      "Skapa nytt arrangemang"
    );
  };

  useEffect(() => {
    getEntries();
    // eslint-disable-next-line
  }, []);

  const filteredEntries = useMemo(() => {
    if (!searchString) return eventPlans;
    const s = searchString.toLocaleLowerCase();
    let matches = [];
    eventPlans.forEach((entry) => {
      const search = entry.title.toLowerCase().search(s);
      if (search !== -1) matches.push(entry);
    });
    return matches;
  }, [eventPlans, searchString]);

  return (
    <StyledEventPlans className="content ptop2">
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="events-top">
            <h2 className="m0 h2">Arrangemang</h2>
            <div className="events-top-right">
              <Input
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                type="text"
                placeholder="Sök arrangemang"
                className="search-input"
              />
              <IconButton iconRight="plus" onClick={createEntryInit}>
                Skapa ny
              </IconButton>
            </div>
          </div>
          <div className="entries">
            {filteredEntries.length ? (
              filteredEntries.map((entry) => (
                <EventPlanCard
                  key={entry._id}
                  entry={entry}
                  deleteInit={deleteEntryInit}
                  editInit={editEntryInit}
                />
              ))
            ) : (
              <p>Inga arrangemang hittades</p>
            )}
          </div>
        </>
      )}
    </StyledEventPlans>
  );
};

export default EventPlans;
